/**
 * BIGWA Core - Network Errors
 * 
 * Erros relacionados a operações de rede.
 */

import { AppError, ErrorContext, ErrorSeverity } from './AppError';

// ============================================================================
// ERRO DE REDE GENÉRICO
// ============================================================================

/**
 * Erro de conexão/rede genérico
 */
export class NetworkError extends AppError {
  readonly code = 'NETWORK_ERROR';
  readonly category = 'network' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
  readonly recoverable = true;

  constructor(
    message: string = 'Network request failed',
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, options);
  }
}

// ============================================================================
// TIMEOUT
// ============================================================================

/**
 * Erro de timeout de requisição
 */
export class TimeoutError extends AppError {
  readonly code = 'TIMEOUT_ERROR';
  readonly category = 'network' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'A requisição demorou muito. Tente novamente.';
  readonly recoverable = true;

  constructor(
    message: string = 'Request timed out',
    public readonly timeoutMs?: number,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, {
      ...options,
      context: { ...options?.context, timeoutMs },
    });
  }
}

// ============================================================================
// ERROS HTTP ESPECÍFICOS
// ============================================================================

/**
 * Erro HTTP genérico com status code
 */
export class HttpError extends AppError {
  readonly code: string;
  readonly category = 'network' as const;
  readonly severity: ErrorSeverity;
  readonly userMessage: string;
  readonly recoverable: boolean;

  constructor(
    public readonly statusCode: number,
    message: string,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, options);
    
    this.code = `HTTP_${statusCode}`;
    
    // Define severidade e recuperabilidade baseado no status
    if (statusCode >= 500) {
      this.severity = 'high';
      this.recoverable = true;
      this.userMessage = 'O servidor está com problemas. Tente novamente em alguns minutos.';
    } else if (statusCode === 429) {
      this.severity = 'medium';
      this.recoverable = true;
      this.userMessage = 'Muitas requisições. Aguarde um momento e tente novamente.';
    } else if (statusCode === 404) {
      this.severity = 'low';
      this.recoverable = false;
      this.userMessage = 'Recurso não encontrado.';
    } else if (statusCode === 403) {
      this.severity = 'medium';
      this.recoverable = false;
      this.userMessage = 'Você não tem permissão para acessar este recurso.';
    } else if (statusCode === 401) {
      this.severity = 'medium';
      this.recoverable = true;
      this.userMessage = 'Sua sessão expirou. Faça login novamente.';
    } else {
      this.severity = 'medium';
      this.recoverable = true;
      this.userMessage = 'Erro na requisição. Tente novamente.';
    }
  }
}

/**
 * Erro 401 - Não autenticado
 */
export class UnauthorizedError extends AppError {
  readonly code = 'UNAUTHORIZED';
  readonly category = 'auth' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'Sua sessão expirou. Faça login novamente.';
  readonly recoverable = true;

  constructor(
    message: string = 'Authentication required',
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, options);
  }
}

/**
 * Erro 403 - Não autorizado
 */
export class ForbiddenError extends AppError {
  readonly code = 'FORBIDDEN';
  readonly category = 'auth' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'Você não tem permissão para esta ação.';
  readonly recoverable = false;

  constructor(
    message: string = 'Access denied',
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, options);
  }
}

/**
 * Erro 404 - Não encontrado
 */
export class NotFoundError extends AppError {
  readonly code = 'NOT_FOUND';
  readonly category = 'notFound' as const;
  readonly severity: ErrorSeverity = 'low';
  readonly userMessage = 'Recurso não encontrado.';
  readonly recoverable = false;

  constructor(
    message: string = 'Resource not found',
    public readonly resourceType?: string,
    public readonly resourceId?: string,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, {
      ...options,
      context: { ...options?.context, resourceType, resourceId },
    });
  }
}

/**
 * Erro 429 - Rate limit excedido
 */
export class RateLimitError extends AppError {
  readonly code = 'RATE_LIMIT_EXCEEDED';
  readonly category = 'quota' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'Limite de requisições atingido. Aguarde um momento.';
  readonly recoverable = true;

  constructor(
    message: string = 'Rate limit exceeded',
    public readonly retryAfterMs?: number,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, {
      ...options,
      context: { ...options?.context, retryAfterMs },
    });
  }
}

/**
 * Erro de serviço offline/indisponível
 */
export class ServiceUnavailableError extends AppError {
  readonly code = 'SERVICE_UNAVAILABLE';
  readonly category = 'external' as const;
  readonly severity: ErrorSeverity = 'high';
  readonly userMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
  readonly recoverable = true;

  constructor(
    message: string = 'Service unavailable',
    public readonly serviceName?: string,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, {
      ...options,
      context: { ...options?.context, serviceName },
    });
  }
}

/**
 * Erro quando circuit breaker está aberto
 */
export class CircuitBreakerOpenError extends AppError {
  readonly code = 'CIRCUIT_BREAKER_OPEN';
  readonly category = 'external' as const;
  readonly severity: ErrorSeverity = 'medium';
  readonly userMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
  readonly recoverable = true;

  constructor(
    message: string = 'Circuit breaker is open',
    public readonly serviceName?: string,
    public readonly resetTimeMs?: number,
    options?: { context?: ErrorContext; cause?: Error }
  ) {
    super(message, {
      ...options,
      context: { ...options?.context, serviceName, resetTimeMs },
    });
  }
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Cria erro apropriado baseado no status HTTP
 */
export const createHttpError = (
  statusCode: number,
  message?: string,
  context?: ErrorContext
): AppError => {
  const defaultMessage = `HTTP ${statusCode}`;
  
  switch (statusCode) {
    case 401:
      return new UnauthorizedError(message ?? defaultMessage, { context });
    case 403:
      return new ForbiddenError(message ?? defaultMessage, { context });
    case 404:
      return new NotFoundError(message ?? defaultMessage, undefined, undefined, { context });
    case 429:
      return new RateLimitError(message ?? defaultMessage, undefined, { context });
    case 503:
      return new ServiceUnavailableError(message ?? defaultMessage, undefined, { context });
    default:
      return new HttpError(statusCode, message ?? defaultMessage, { context });
  }
};

/**
 * Verifica se erro é de rede
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;
  if (error instanceof HttpError) return true;
  if (error instanceof TimeoutError) return true;
  return false;
};

/**
 * Verifica se erro é de autenticação
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof UnauthorizedError) return true;
  if (error instanceof ForbiddenError) return true;
  if (error instanceof HttpError && (error.statusCode === 401 || error.statusCode === 403)) {
    return true;
  }
  return false;
};

/**
 * Verifica se deve tentar novamente
 */
export const shouldRetry = (error: unknown): boolean => {
  // Erros de rede são retentáveis
  if (error instanceof NetworkError) return true;
  if (error instanceof TimeoutError) return true;
  
  // Rate limit - sim, mas com delay
  if (error instanceof RateLimitError) return true;
  
  // 5xx - servidor com problemas, tentar novamente
  if (error instanceof HttpError && error.statusCode >= 500) return true;
  
  // 4xx geralmente não são retentáveis (exceto 429)
  if (error instanceof HttpError && error.statusCode >= 400 && error.statusCode < 500) {
    return error.statusCode === 429;
  }
  
  return false;
};
